// A learning-agent conversation's name and who can see it, on the run itself.
//
// A run is titled from the first line of its opening message, so titles are
// routinely a full sentence. The title therefore RENDERS AS TEXT that wraps -
// an always-on input showed a fixed-width slice of it and scrolled the rest out
// of view, which is worse than the list it is meant to head. Renaming is an
// explicit mode, entered from a button.
//
// Shared by both run detail routes.
import { useEffect, useRef, useState } from "react";

import {
  useRegenerateStatefulRunName,
  useRenameStatefulRun,
  useSetStatefulRunPrivacy,
  type StatefulRun,
} from "../api/hooks/useStatefulAgents";
import { DeleteConversation } from "./DeleteConversation";
import styles from "./RunTitleBar.module.css";

export const RunTitleBar = ({
  run,
  onDeleted,
}: {
  run: StatefulRun;
  /** Where the operator goes once they have deleted it: back to the list it was in. */
  onDeleted: () => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(run.title);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const rename = useRenameStatefulRun(run.run_id);
  const regenerate = useRegenerateStatefulRunName(run.run_id);
  const setPrivacy = useSetStatefulRunPrivacy(run.run_id);

  // A regenerate rewrites the title server-side, and the run polls - so follow
  // the stored title, except while the operator is mid-edit.
  useEffect(() => {
    if (!editing) setDraft(run.title);
  }, [run.title, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const busy = rename.isLoading || regenerate.isLoading || setPrivacy.isLoading;

  const act = async (call: () => Promise<unknown>, failure: string) => {
    setError(null);
    try {
      await call();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : failure);
      return false;
    }
  };

  const save = async () => {
    const next = draft.trim();
    if (!next || next === run.title) {
      setDraft(run.title);
      setEditing(false);
      return;
    }
    const ok = await act(
      () => rename.mutateAsync({ title: next }),
      "Could not rename this conversation.",
    );
    if (ok) setEditing(false);
    else setDraft(run.title);
  };

  return (
    <div className={styles.bar}>
      {editing ? (
        // A textarea, not an input: these titles are sentence-length, and the
        // point of this change is that the whole of one is visible at once.
        <textarea
          ref={inputRef}
          className={styles.titleInput}
          value={draft}
          rows={2}
          disabled={busy}
          aria-label="Conversation title"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              void save();
            }
            if (event.key === "Escape") {
              setDraft(run.title);
              setEditing(false);
            }
          }}
        />
      ) : (
        <h2 className={styles.title}>{run.title || "Untitled conversation"}</h2>
      )}

      <div className={styles.actions}>
        {editing ? (
          <>
            <button
              type="button"
              className={styles.btn}
              disabled={busy || !draft.trim()}
              onClick={() => void save()}
            >
              {rename.isLoading ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              className={styles.btn}
              disabled={busy}
              onClick={() => {
                setDraft(run.title);
                setEditing(false);
                setError(null);
              }}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={styles.btn}
              disabled={busy}
              onClick={() => setEditing(true)}
            >
              Rename
            </button>
            <button
              type="button"
              className={styles.btn}
              disabled={busy}
              title="Name this conversation from what it says"
              onClick={() => void act(() => regenerate.mutateAsync(), "Could not generate a name.")}
            >
              {regenerate.isLoading ? "Naming…" : "Auto-name"}
            </button>
          </>
        )}

        {/* Only the person who started it may change who sees it; everyone else
            is told it is private rather than shown a control that would fail. */}
        {run.is_owner ? (
          <label className={styles.private}>
            <input
              type="checkbox"
              checked={!!run.is_private}
              disabled={busy}
              onChange={(event) =>
                void act(
                  () => setPrivacy.mutateAsync({ is_private: event.target.checked }),
                  "Could not change who can see this conversation.",
                )
              }
            />
            Private
          </label>
        ) : (
          run.is_private && <span className={styles.badge}>Private</span>
        )}

        <DeleteConversation
          run={run}
          trigger="text"
          notOwner="absent"
          className={styles.btn}
          dangerClassName={styles.danger}
          disabled={busy}
          onDeleted={onDeleted}
          onError={setError}
        />
        {run.hidden_at && <span className={styles.badge}>Deleted</span>}
      </div>

      {run.is_private && (
        <p className={styles.note}>
          Only you can see this transcript. What the agent learned here still applies for
          everyone, and its cost still shows on the costs page.
        </p>
      )}
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
};
